// module imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// file imports
import axiosInstance from '@/lib/axios';
import { IReport } from '@/types';

const addFeedback = createAsyncThunk('feedback/addFeedback', async ({ slug, answers, user, consentToShare, consentToShowName }: { slug: string; answers: any; user?: { name: string; email: string; comments?: string }; consentToShare?: boolean; consentToShowName?: boolean }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(`/feedback/${slug}`, { answers, user, consentToShare, consentToShowName });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Adding feedback failed');
  }
});

const updateFeedback = createAsyncThunk('feedback/updateFeedback', async ({ slug, feedbackId, status }: { slug: string; feedbackId: string; status: string }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch(`/feedback/${slug}?feedbackId=${feedbackId}`, { status });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Updating feedback failed');
  }
});

const getFeedbackReports = createAsyncThunk('feedback/getFeedbackReports', async (slug: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/feedback/${slug}/report`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving feedback reports failed');
  }
});

const getFeedbackReportDetails = createAsyncThunk('feedback/getFeedbackReportDetails', async ({ slug, submissionId }: { slug: string; submissionId: string }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/feedback/${slug}/report-details?submissionId=${submissionId}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving feedback report details failed');
  }
});

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState: {
    data: null as IReport | null,
    loading: false,
    error: null as string | null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // addFeedback
      .addCase(addFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addFeedback.fulfilled, (state) => {
        state.loading = false;
        // state.data = action.payload;
      })
      .addCase(addFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateFeedback
      .addCase(updateFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFeedback.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(updateFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getFeedbackReports
      .addCase(getFeedbackReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFeedbackReports.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getFeedbackReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getFeedbackReportDetails
      .addCase(getFeedbackReportDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFeedbackReportDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getFeedbackReportDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export { addFeedback, getFeedbackReportDetails, getFeedbackReports, updateFeedback };
export default feedbackSlice.reducer;
